import { convertMeasurement, measurementUnit, parseMeasurementUnit } from '../lib/measurement'
import type { MeasurementSystem } from '../lib/measurementSystem'

export function productExportView(products: Product[], system: MeasurementSystem): Product[] {
    const dimension_unit = measurementUnit(system, 'cargoLength'), weight_unit = measurementUnit(system, 'mass')
    return products.map(product => ({ ...product, dimension_unit, weight_unit,
        ...Object.fromEntries(['length', 'width', 'height'].map(field => [field,
            (product as any)[field] == null ? null : convertMeasurement(Number((product as any)[field]), parseMeasurementUnit(product.dimension_unit || 'mm'), dimension_unit)
        ])),
        weight: product.weight == null ? null : convertMeasurement(Number(product.weight), parseMeasurementUnit(product.weight_unit || 'kg'), weight_unit),
    })) as Product[]
}
import { Product } from '../services/product.service'
import { jsPDF } from 'jspdf'

/**
 * Exporte la liste des produits au format JSON
 */
export function exportProductsToJson(products: Product[], filename?: string) {
    const dataToExport = products.map(p => ({
        ...p,
        id: p.id,
        name: p.name,
        sku: p.sku || null,
        description: p.description || null,
        status: p.status || 'active',
        favorite_rating: p.favorite_rating || 0,
        dimensions: {
            length: p.length ?? null,
            width: p.width ?? null,
            height: p.height ?? null,
            unit: p.dimension_unit || 'mm'
        },
        weight: {
            value: p.weight ?? null,
            unit: p.weight_unit || 'kg'
        },
        stacking: {
            stackable: p.stackable ?? true,
            no_stack_above: p.no_stack_above ?? false,
            max_stack_weight: p.max_stack_weight ?? null,
            max_stack_layer: p.max_stack_layer ?? null,
        },
        constraints: {
            is_dangerous_goods: p.is_dangerous_goods ?? false,
            dangerous_goods_class: p.dangerous_goods_class ?? null,
            un_number: p.un_number ?? null,
            temperature_controlled: p.temperature_controlled ?? false,
            min_temperature: p.min_temperature ?? null,
            max_temperature: p.max_temperature ?? null,
            must_be_on_floor: p.must_be_on_floor ?? false,
            rotatable: p.rotatable ?? true,
        },
        pallet_specs: p.pallet_type ? {
            pallet_type: p.pallet_type,
            pallet_custom_length: p.pallet_custom_length ?? null,
            pallet_custom_width: p.pallet_custom_width ?? null,
            max_pallet_height_mm: p.max_pallet_height_mm ?? null,
            max_pallet_weight_kg: p.max_pallet_weight_kg ?? null,
        } : null,
        created_at: p.created_at || null,
        updated_at: p.updated_at || null
    }))

    const jsonStr = JSON.stringify(dataToExport, null, 2)
    const blob = new Blob([jsonStr], { type: 'application/json;charset=utf-8;' })
    const name = filename || `catalogue_produits_tms_${new Date().toISOString().slice(0, 10)}.json`

    downloadBlob(blob, name)
}

/**
 * Exporte la liste des produits au format CSV (compatible Excel UTF-8 BOM)
 */
export function exportProductsToCsv(products: Product[], filename?: string, system: MeasurementSystem = 'metric') {
    products = productExportView(products, system)
    const headers = [
        'ID',
        'SKU',
        'Nom du Produit',
        'Description',
        'Statut',
        'Favori',
        'Longueur',
        'Largeur',
        'Hauteur',
        'Unité Dim.',
        'Poids',
        'Unité Poids',
        'Matière Dangereuse (ADR)',
        'Classe ADR',
        'N° UN',
        'Contrôle Température',
        'Temp. Min (°C)',
        'Temp. Max (°C)',
        'Empilable',
        'Ne pas superposer',
        'Au Sol Uniquement',
        'Rotation Autorisée',
        'Type Palette'
    ]

    const rows = products.map(p => {
        const isNonStackable = p.stackable === false || p.no_stack_above === true
        return [
            p.id || '',
            p.sku || '',
            p.name || '',
            p.description || '',
            p.status === 'active' ? 'Actif' : p.status === 'draft' ? 'Brouillon' : 'Inactif',
            p.favorite_rating ? `${p.favorite_rating}/5` : '0',
            p.length ?? '',
            p.width ?? '',
            p.height ?? '',
            p.dimension_unit || 'mm',
            p.weight ?? '',
            p.weight_unit || 'kg',
            p.is_dangerous_goods ? 'Oui' : 'Non',
            p.dangerous_goods_class || '',
            p.un_number || '',
            p.temperature_controlled ? 'Oui' : 'Non',
            p.min_temperature ?? '',
            p.max_temperature ?? '',
            p.stackable !== false ? 'Oui' : 'Non',
            isNonStackable ? 'Oui' : 'Non',
            p.must_be_on_floor ? 'Oui' : 'Non',
            p.rotatable !== false ? 'Oui' : 'Non',
            p.pallet_type || ''
        ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(';')
    })

    // Add UTF-8 BOM for Microsoft Excel compatibility
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const name = filename || `catalogue_produits_tms_${new Date().toISOString().slice(0, 10)}.csv`

    downloadBlob(blob, name)
}

/**
 * Exporte la liste des produits au format PDF (Rapport Catalogue TMS)
 */
export function exportProductsToPdf(products: Product[], filename?: string, titleFilterNote?: string, system: MeasurementSystem = 'metric') {
    products = productExportView(products, system)
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
    })

    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const dateStr = new Date().toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })

    // Colors
    const darkSlate = [15, 23, 42] // #0f172a
    const orangeAccent = [249, 100, 22] // #f97316
    const lightBg = [248, 250, 252] // #f8fafc
    const textDark = [30, 41, 59] // #1e293b
    const textMuted = [100, 116, 139] // #64748b

    // 1. Header Banner
    doc.setFillColor(darkSlate[0], darkSlate[1], darkSlate[2])
    doc.rect(0, 0, pageWidth, 24, 'F')

    doc.setFillColor(orangeAccent[0], orangeAccent[1], orangeAccent[2])
    doc.rect(0, 24, pageWidth, 2, 'F')

    doc.setTextColor(255, 255, 255)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('CATALOGUE PRODUITS - SalmaLoad', 12, 13)

    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(203, 213, 225)
    doc.text(`Rapport généré le ${dateStr} | ${products.length} référence(s)`, pageWidth - 12, 13, { align: 'right' })

    let yPos = 33

    // Subtitle filter note if provided
    if (titleFilterNote) {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(textMuted[0], textMuted[1], textMuted[2])
        doc.text(`Filtre appliqué : ${titleFilterNote}`, 12, yPos)
        yPos += 7
    }

    // 2. Summary KPI Box
    const totalCount = products.length
    const activeCount = products.filter(p => p.status === 'active').length
    const adrCount = products.filter(p => p.is_dangerous_goods).length
    const coldCount = products.filter(p => p.temperature_controlled).length

    doc.setFillColor(lightBg[0], lightBg[1], lightBg[2])
    doc.setDrawColor(226, 232, 240)
    doc.roundedRect(12, yPos, pageWidth - 24, 16, 2, 2, 'FD')

    const colWidth = (pageWidth - 24) / 4
    const kpis = [
        { label: 'RÉFÉRENCES', val: `${totalCount}` },
        { label: 'ACTIFS', val: `${activeCount}` },
        { label: 'MAT. DANGEREUSES (ADR)', val: `${adrCount}` },
        { label: 'CHAÎNE DU FROID', val: `${coldCount}` },
    ]

    kpis.forEach((kpi, idx) => {
        const x = 12 + idx * colWidth + colWidth / 2
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2])
        doc.text(kpi.val, x, yPos + 6, { align: 'center' })

        doc.setFontSize(7)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(textMuted[0], textMuted[1], textMuted[2])
        doc.text(kpi.label, x, yPos + 12, { align: 'center' })
    })

    yPos += 22

    // 3. Table Header Function
    const drawTableHeader = (currY: number) => {
        doc.setFillColor(darkSlate[0], darkSlate[1], darkSlate[2])
        doc.rect(12, currY, pageWidth - 24, 8, 'F')

        doc.setFontSize(8)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(255, 255, 255)

        doc.text('#', 15, currY + 5.5)
        doc.text('SKU / Code', 25, currY + 5.5)
        doc.text('Nom du produit', 55, currY + 5.5)
        doc.text('Dimensions (L×l×H)', 100, currY + 5.5)
        doc.text('Masse', 140, currY + 5.5)
        doc.text('Statut', 158, currY + 5.5)
        doc.text('Spécificités', 178, currY + 5.5)

        return currY + 8
    }

    yPos = drawTableHeader(yPos)

    // 4. Products Table Body
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')

    products.forEach((product, idx) => {
        // Check if new page is needed
        if (yPos > pageHeight - 25) {
            doc.addPage()
            // Redraw header banner & table header
            doc.setFillColor(darkSlate[0], darkSlate[1], darkSlate[2])
            doc.rect(0, 0, pageWidth, 12, 'F')
            doc.setFillColor(orangeAccent[0], orangeAccent[1], orangeAccent[2])
            doc.rect(0, 12, pageWidth, 1.5, 'F')
            doc.setTextColor(255, 255, 255)
            doc.setFontSize(9)
            doc.setFont('helvetica', 'bold')
            doc.text('CATALOGUE PRODUITS - SalmaLoad (Suite)', 12, 8)

            yPos = 20
            yPos = drawTableHeader(yPos)
            doc.setFontSize(8)
            doc.setFont('helvetica', 'normal')
        }

        const isEven = idx % 2 === 0
        const rowHeight = 18

        if (isEven) {
            doc.setFillColor(248, 250, 252)
            doc.rect(12, yPos, pageWidth - 24, rowHeight, 'F')
        }

        doc.setDrawColor(241, 245, 249)
        doc.line(12, yPos + rowHeight, pageWidth - 12, yPos + rowHeight)

        doc.setTextColor(textDark[0], textDark[1], textDark[2])

        // Row Number
        doc.setFont('helvetica', 'bold')
        doc.text(`${idx + 1}`, 15, yPos + 6)

        // SKU
        doc.setFont('helvetica', 'normal')
        const skuText = doc.splitTextToSize(product.sku || '—', 32)[0]
        doc.text(skuText, 25, yPos + 6)

        // Name
        doc.setFont('helvetica', 'bold')
        const nameText = doc.splitTextToSize(product.name || '—', 42)[0]
        doc.text(nameText, 55, yPos + 6)

        // Dimensions
        doc.setFont('helvetica', 'normal')
        const dims = product.length && product.width && product.height
            ? `${Number(Number(product.length).toFixed(3))}×${Number(Number(product.width).toFixed(3))}×${Number(Number(product.height).toFixed(3))} ${product.dimension_unit || 'mm'}`
            : '—'
        doc.text(doc.splitTextToSize(dims, 37), 100, yPos + 6)

        // Weight
        const weightStr = product.weight != null ? `${Number(Number(product.weight).toFixed(3))} ${product.weight_unit || 'kg'}` : '—'
        doc.text(doc.splitTextToSize(weightStr, 16), 140, yPos + 6)

        // Status
        const statusText = product.status === 'active' ? 'Actif' : product.status === 'draft' ? 'Brouillon' : 'Inactif'
        if (product.status === 'active') doc.setTextColor(16, 185, 129)
        else if (product.status === 'draft') doc.setTextColor(245, 158, 11)
        else doc.setTextColor(148, 163, 184)
        doc.text(statusText, 158, yPos + 6)

        // Specificities / Badges text
        doc.setTextColor(textDark[0], textDark[1], textDark[2])
        const tags: string[] = []
        if (product.is_dangerous_goods) tags.push('ADR')
        if (product.temperature_controlled) tags.push('Froid')
        if (product.stackable === false || product.no_stack_above) tags.push('Non-emp.')
        if (product.must_be_on_floor) tags.push('Au sol')

        const tagStr = tags.length > 0 ? tags.join(', ') : '—'
        doc.text(doc.splitTextToSize(tagStr, 19), 178, yPos + 6)

        yPos += rowHeight
    })

    // 5. Page Numbers Footers
    const totalPages = doc.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setDrawColor(226, 232, 240)
        doc.line(12, pageHeight - 12, pageWidth - 12, pageHeight - 12)

        doc.setFontSize(7)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(textMuted[0], textMuted[1], textMuted[2])
        doc.text('TMS 3D Load Optimizer — Document confidentiel d\'exploitation logistique', 12, pageHeight - 6)
        doc.text(`Page ${i} sur ${totalPages}`, pageWidth - 12, pageHeight - 6, { align: 'right' })
    }

    const name = filename || `catalogue_produits_tms_${new Date().toISOString().slice(0, 10)}.pdf`
    const pdfBytes = doc.output('arraybuffer')
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })

    downloadBlob(blob, name)
}

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}
